/**
 * Workflow Designer Page
 * Full-page workflow editor with three-panel layout
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import WorkflowDesigner from '../components/WorkflowDesigner';
import { agentClient } from '@/lib/agentClient';
import type { VolcanoWorkflow } from '../types/volcano.types';

export default function WorkflowDesignerPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentName, setAgentName] = useState('New Volcano Agent');
  const [workflow, setWorkflow] = useState<VolcanoWorkflow | null>(null);
  const isNewAgent = agentId === 'new';

  useEffect(() => {
    if (agentId === 'new') {
      // Initialize empty workflow for new agent
      setWorkflow({
        version: '1.0',
        steps: []
      });
      setLoading(false);
    } else if (agentId) {
      loadAgent();
    }
  }, [agentId]);

  const loadAgent = async () => {
    if (!agentId) return;

    try {
      setLoading(true);
      setError(null);

      // Discover agent to get workflow
      const agents = await agentClient.discoverAgents();
      const agent = agents.find((a) => a.id === agentId);

      if (!agent) {
        setError('Agent not found');
        return;
      }

      if (!agent.volcano_workflow) {
        setError('This agent does not have a Volcano workflow');
        return;
      }

      setAgentName(agent.name);
      setWorkflow(agent.volcano_workflow);
    } catch (err: any) {
      setError(err.message || 'Failed to load agent');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (updatedWorkflow: VolcanoWorkflow) => {
    if (!agentId) return;

    try {
      if (isNewAgent) {
        // For new agents, we need to create the agent first
        // This would integrate with your agent creation API
        const newAgentData = {
          name: agentName,
          description: 'Volcano workflow agent',
          agent_type: 'internal',
          execution_engine: 'volcano',
          volcano_workflow: updatedWorkflow
        };
        
        // TODO: Implement createAgent API call
        console.log('Creating new agent:', newAgentData);
        alert('Agent creation API not yet implemented. This would save: ' + JSON.stringify(newAgentData, null, 2));
        // navigate('/agents/hub');
      } else {
        const success = await agentClient.updateAgentWorkflow(agentId, updatedWorkflow);
        if (success) {
          navigate('/agents/hub');
        } else {
          throw new Error('Failed to update workflow');
        }
      }
    } catch (err: any) {
      console.error('Save error:', err);
      alert('Error saving agent: ' + err.message);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Unknown error'}</AlertDescription>
        </Alert>
        <Button className="mt-4" onClick={() => navigate('/agents/hub')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Agents
        </Button>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Top Bar */}
      <div className="bg-white border-b px-6 py-3 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          {isNewAgent ? (
            <input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              className="text-xl font-bold text-gray-900 border-b-2 border-transparent hover:border-blue-300 focus:border-blue-500 focus:outline-none bg-transparent px-2 py-1"
              placeholder="Agent Name..."
            />
          ) : (
            <h1 className="text-xl font-bold text-gray-900">{agentName}</h1>
          )}
          <p className="text-sm text-gray-600">
            {isNewAgent ? '🌋 Create New Volcano Workflow' : 'Edit Workflow'}
          </p>
        </div>
      </div>

      {/* Workflow Designer */}
      <div className="flex-1 overflow-hidden">
        <WorkflowDesigner
          workflow={workflow}
          agentId={agentId!}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
}

